function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { Card, CardBody, Button, Spinner } from 'reactstrap';
import { getValue } from '@truqc/content-path';
import Badge from './badge';
import saveFormBehavior from '../../../../nativeBehaviors/saveFormBehavior';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import aiNativeBehaviorEvaluator from '../../../../nativeBehaviors/aiNativeBehaviorEvaluator';
import { arrayToCollection, collectionToArray, ensureLimitedResultsPrefix } from './qualityAgentHelpers';
import QualityAgentCompletionModal from './qualityAgentCompletionModal';
import QualityAgentErrorModal from './qualityAgentErrorModal';

// Run Quality Agent Button component for empty state
const RunQualityAgentButton = ({
  content: _content = null,
  path,
  formActions,
  reduxEntity,
  pendingOperation: _pendingOperation = false,
  notificationActions,
  onOpenSettings: _onOpenSettings = () => {}
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [completedSummary, setCompletedSummary] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // Get settings and count enabled checks
  const settingsPath = `${path}/settings`;
  const settings = getValue(settingsPath, _content) || {};
  const checksCollection = settings.checks || {};
  const enabledChecksCount = Object.values(checksCollection).filter(check => check.enabled !== false).length;

  // Format checks enabled text
  const checksEnabledText = enabledChecksCount === 1 ? '1 check enabled' : `${enabledChecksCount} checks enabled`;
  const runQualityAgent = useCallback(async () => {
    if (_pendingOperation) {
      notificationActions.info({
        message: 'Waiting for changes to save.'
      }, {
        autoClose: 3000
      });
      return;
    }
    setIsRunning(true);
    try {
      var _content$formType, _report$summary, _report$summary2, _report$summary3, _report$summary4;
      // Get form title and type for the limited results prefix
      const formTitle = (_content === null || _content === void 0 ? void 0 : _content.title) || '';
      const formTypeName = (_content === null || _content === void 0 || (_content$formType = _content.formType) === null || _content$formType === void 0 ? void 0 : _content$formType.name) || '';

      // Convert checks collection to array, filter by enabled, ensure correct prefix
      const inputChecks = collectionToArray(checksCollection).filter(check => check.enabled !== false) // default to enabled if not set
      .map(check => _objectSpread(_objectSpread({}, check), {}, {
        query: ensureLimitedResultsPrefix(check.query || '', formTypeName, formTitle)
      }));
      if (inputChecks.length === 0) {
        notificationActions.info({
          message: 'No quality agent checks enabled. Please enable checks in settings.'
        }, {
          autoClose: 5000
        });
        setIsRunning(false);
        return;
      }

      // Call qualityAgent directly via AI service (no server pass-through needed)
      // Authentication is handled automatically via JWT token
      const response = await aiNativeBehaviorEvaluator.qualityAgent(inputChecks);

      // Transform arrays to collections
      const {
        report
      } = response;
      const resultChecksCollection = arrayToCollection(report.checks || []);
      const suggestedFollowupsCollection = arrayToCollection(report.suggestedFollowups || []);

      // Build the content to save (preserving settings, initializing priorRuns)
      const newSummary = {
        timestamp: report.timestamp,
        assessment: report.assessment,
        assessmentMessage: report.assessmentMessage,
        totalChecks: (_report$summary = report.summary) === null || _report$summary === void 0 ? void 0 : _report$summary.totalChecks,
        passed: (_report$summary2 = report.summary) === null || _report$summary2 === void 0 ? void 0 : _report$summary2.passed,
        failed: (_report$summary3 = report.summary) === null || _report$summary3 === void 0 ? void 0 : _report$summary3.failures,
        warnings: (_report$summary4 = report.summary) === null || _report$summary4 === void 0 ? void 0 : _report$summary4.warnings
      };
      const qualityAgentContent = {
        settings,
        priorRuns: {},
        summary: newSummary,
        checks: resultChecksCollection,
        suggestedFollowups: suggestedFollowupsCollection
      };

      // Save using formActions.change
      const state = store.getState();
      const {
        active
      } = state[reduxEntity];
      await formActions.change(active.id, `content${path}`, qualityAgentContent);
      // eslint-disable-next-line import/no-named-as-default-member
      await saveFormBehavior.evaluate({}, reduxEntity);

      // Show completion modal
      setCompletedSummary(newSummary);
      setIsCompletionModalOpen(true);
    } catch (error) {
      logger('Error running quality agent:', error);
      setIsErrorModalOpen(true);
    } finally {
      setIsRunning(false);
    }
    // settings is derived from content/path; checksCollection from settings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_pendingOperation, notificationActions, _content, path, formActions, reduxEntity, checksCollection]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    className: "shadow-sm"
  }, /*#__PURE__*/React.createElement(CardBody, {
    className: "text-center py-5"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-center mx-auto mb-3"
  }, /*#__PURE__*/React.createElement("img", {
    src: "/img/logos/truCheckmark.png",
    alt: "TRU Logo",
    className: "quality-agent__logo"
  })), /*#__PURE__*/React.createElement("h5", {
    className: "mb-2"
  }, "Traceable. Reliable. Understandable."), /*#__PURE__*/React.createElement("p", {
    className: "text-muted mb-4"
  }, "Run automated quality checks on your data to identify issues and ensure data integrity."), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-center gap-3 mb-4"
  }, /*#__PURE__*/React.createElement(Badge, {
    tag: "span",
    color: enabledChecksCount > 0 ? 'success' : 'secondary',
    className: "px-3 py-2 quality-agent__badge-text"
  }, checksEnabledText), /*#__PURE__*/React.createElement(Button, {
    color: "outline-secondary",
    size: "sm",
    onClick: _onOpenSettings
  }, "Settings")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    size: "lg",
    onClick: runQualityAgent,
    disabled: isRunning || _pendingOperation || enabledChecksCount === 0
  }, isRunning ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spinner, {
    size: "sm",
    className: "me-2"
  }), "Running Quality Agent Analysis...") : 'Run Quality Agent Analysis'))), completedSummary && /*#__PURE__*/React.createElement(QualityAgentCompletionModal, {
    isOpen: isCompletionModalOpen,
    onClose: () => setIsCompletionModalOpen(false),
    summary: completedSummary
  }), /*#__PURE__*/React.createElement(QualityAgentErrorModal, {
    isOpen: isErrorModalOpen,
    onClose: () => setIsErrorModalOpen(false)
  }));
};
RunQualityAgentButton.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    formType: PropTypes.shape({
      name: PropTypes.string,
      identifier: PropTypes.string
    })
  }),
  path: PropTypes.string.isRequired,
  formActions: PropTypes.shape({
    change: PropTypes.func.isRequired
  }).isRequired,
  reduxEntity: PropTypes.string.isRequired,
  pendingOperation: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func.isRequired
  }).isRequired,
  onOpenSettings: PropTypes.func
};

// Removed defaultProps - using default parameters in function signature instead

export default RunQualityAgentButton;